import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { PersistGate } from "redux-persist/integration/react";
import { Provider } from "react-redux";

import { persistor, store } from "@/store";

import { datadogRum } from "@datadog/browser-rum";

import App from "./App.tsx";

import { EMAIL_OF_INTERNAL_TESTER } from "./constants/user.ts";

import "./index.css";

const runDatadogAnalytics =
  import.meta.env.VITE_ENABLE_DATA_DOG_ANALYTICS === "true";

if (runDatadogAnalytics) {
  datadogRum.init({
    applicationId: import.meta.env.VITE_DATA_DOG_APPLICATION_ID,
    clientToken: import.meta.env.VITE_DATA_DOG_CLIENT_TOKEN,
    site: import.meta.env.VITE_DATA_DOG_SITE,
    service: import.meta.env.VITE_APP_NAME,
    version: import.meta.env.VITE_APP_VERSION,
    env: import.meta.env.MODE,
    sessionSampleRate: Number(import.meta.env.VITE_SESSION_SAMPLE_RATE ?? 100),
    sessionReplaySampleRate: Number(
      import.meta.env.VITE_SESSION_REPLAY_SAMPLE_RATE ?? 100
    ),
    defaultPrivacyLevel: "mask-user-input",
    beforeSend: (event) => event?.usr?.id !== EMAIL_OF_INTERNAL_TESTER // exclude internal tester
  });
}

async function enableMocking() {
  if (import.meta.env.VITE_API_MOCKING === "enabled") {
    const { worker } = await import("./mocks/browser.ts");

    return worker.start({
      serviceWorker: {
        url: `${import.meta.env.VITE_BASE_PATH || "/"}mockServiceWorker.js`
      },
      onUnhandledRequest: "warn"
    });
  }
}

// TODO: reenable when the service worker config is ready
// const registerServiceWorker = async () => {
//   if (import.meta.env.PROD && "serviceWorker" in navigator) {
//     try {
//       const basePath = import.meta.env.VITE_BASE_PATH || "/";
//       const normalizedBasePath = basePath.startsWith("/")
//         ? basePath
//         : `/${basePath}`;
//       const servicePath = `${normalizedBasePath}service_worker.js`.replace(
//         "//",
//         "/"
//       );

//       const registration = await navigator.serviceWorker.register(servicePath, {
//         scope: normalizedBasePath.endsWith("/")
//           ? normalizedBasePath
//           : `${normalizedBasePath}/`
//       });
//       if (registration.installing) {
//         console.log("Service worker installing");
//       } else if (registration.waiting) {
//         console.log("Service worker installed");
//       } else if (registration.active) {
//         console.log("Service worker active");
//       }
//     } catch (error) {
//       console.error(`Registration failed with ${error}`);
//     }
//   }
// };
// registerServiceWorker();

const unregisterServiceWorker = () => {
  if (import.meta.env.PROD && "serviceWorker" in navigator) {
    const basePath = import.meta.env.VITE_BASE_PATH || "/";
    const normalizedBasePath = basePath.startsWith("/")
      ? basePath
      : `/${basePath}`;

    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        // Check if the service worker scope matches our application's base path
        if (
          registration.scope.includes(
            `${window.location.origin}${normalizedBasePath}`
          )
        ) {
          console.log(
            `Unregistering service worker with scope: ${registration.scope}`
          );
          registration.unregister();
        }
      });
    });
  }
};

unregisterServiceWorker();

enableMocking().then(() => {
  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <App />
        </PersistGate>
      </Provider>
    </StrictMode>
  );
});
